// ─────────────────────────────────────────────────────────────
//  XYLO — Database Layer (Firestore) — SECURED
//  All wallet read/write operations live here
// ─────────────────────────────────────────────────────────────

import {
  doc, getDoc, setDoc, updateDoc,
  collection, addDoc, getDocs,
  query, orderBy, onSnapshot, where,
  serverTimestamp, increment, Timestamp
} from "firebase/firestore";
import { db } from "./firebase";
export { db };

// ── Constants ─────────────────────────────────────────────────
const DAILY_SEND_LIMIT   = 100000; // max Rs. 1,00,000/day
const MAX_TX_PER_HOUR    = 10;    // max 10 sends per hour
const MAX_SINGLE_TX      = 100000; // max Rs. 1,00,000 per single transaction

// ── User profile ─────────────────────────────────────────────

export async function createUserProfile(uid, data) {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      name:        data.name,
      email:       data.email,
      avatar:      data.avatar,
      userId:      `XYL-${Math.floor(1000 + Math.random() * 9000)}-${data.avatar}`,
      balance:     0,
      bank:        null,
      pin:         null,           // 6-digit PIN hash stored here
      dailySent:   0,              // resets every day
      dailySentDate: null,         // date string of last reset
      since:       new Date().toLocaleDateString("en-US", { month:"long", year:"numeric" }),
      loginMethod: data.loginMethod,
      createdAt:   serverTimestamp(),
    });
  }
  return (await getDoc(ref)).data();
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateUserProfile(uid, data) {
  await updateDoc(doc(db, "users", uid), data);
}

// ── PIN Management ────────────────────────────────────────────

// Simple hash for PIN (not cryptographic — PIN is just a UX lock)
function hashPin(pin) {
  let hash = 0;
  for (let i = 0; i < pin.length; i++) {
    const char = pin.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString();
}

export async function setPin(uid, pin) {
  if (!/^\d{6}$/.test(pin)) throw new Error("PIN must be exactly 6 digits");
  await updateDoc(doc(db, "users", uid), { pin: hashPin(pin) });
}

export async function verifyPin(uid, pin) {
  const snap = await getDoc(doc(db, "users", uid));
  const stored = snap.data()?.pin;
  if (!stored) return false;
  return stored === hashPin(pin);
}

export function hasPin(profile) {
  return !!profile?.pin;
}

// ── Balance ───────────────────────────────────────────────────

export async function updateBalance(uid, amount) {
  await updateDoc(doc(db, "users", uid), {
    balance: increment(amount)
  });
}

// ── Daily limit check ─────────────────────────────────────────

async function checkAndUpdateDailyLimit(uid, amount) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    const data = snap.data();
    const today = new Date().toDateString();

    let dailySent = data.dailySent || 0;
    const dailySentDate = data.dailySentDate;

    // Reset if it's a new day
    if (dailySentDate !== today) {
      dailySent = 0;
    }

    if (dailySent + amount > DAILY_SEND_LIMIT) {
      const remaining = DAILY_SEND_LIMIT - dailySent;
      throw new Error(`Daily limit reached. You can send $${remaining.toFixed(2)} more today. Limit resets at midnight.`);
    }

    // Update daily sent — don't throw if this fails
    try {
      await updateDoc(doc(db, "users", uid), {
        dailySent: dailySentDate !== today ? amount : increment(amount),
        dailySentDate: today,
      });
    } catch(e) {
      console.warn("[XyloPay] Could not update daily limit tracker:", e.message);
      // Don't block payment if tracking update fails
    }
  } catch(e) {
    if (e.message.includes("Daily limit")) throw e;
    console.warn("[XyloPay] Daily limit check error:", e.message);
    // Don't block payment for other errors
  }
}

// ── Rate limit check ──────────────────────────────────────────

async function checkRateLimit(uid) {
  const oneHourAgo = new Date(Date.now() - 3600000);
  const txSnap = await getDocs(query(
    collection(db, "users", uid, "transactions"),
    where("createdAt", ">=", Timestamp.fromDate(oneHourAgo)),
    orderBy("createdAt", "desc")
  ));

  const recentCount = txSnap.docs.filter(d => d.data().type !== "credit").length;
  if (recentCount >= MAX_TX_PER_HOUR) {
    throw new Error(`Too many transactions. Maximum ${MAX_TX_PER_HOUR} sends per hour for security. Try again later.`);
  }
}

// ── Transactions ─────────────────────────────────────────────

export async function addTransaction(uid, tx) {
  const ref = collection(db, "users", uid, "transactions");
  await addDoc(ref, {
    ...tx,
    createdAt: serverTimestamp(),
  });
}

export function subscribeTransactions(uid, callback) {
  const ref = query(
    collection(db, "users", uid, "transactions"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(ref, snap => {
    const txs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(txs);
  });
}

// ── Notifications ─────────────────────────────────────────────

export async function addNotification(uid, notif) {
  await addDoc(collection(db, "users", uid, "notifications"), {
    ...notif,
    read:      false,
    createdAt: serverTimestamp(),
  });
}

export function subscribeNotifications(uid, callback) {
  const ref = query(
    collection(db, "users", uid, "notifications"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(ref, snap => {
    const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(notifs);
  });
}

export async function markAllNotificationsRead(uid, notifIds) {
  const updates = notifIds.map(id =>
    updateDoc(doc(db, "users", uid, "notifications", id), { read: true })
  );
  await Promise.all(updates);
}

// ── Send Payment (SECURED) ────────────────────────────────────

export async function sendPayment({ senderUid, senderData, recipientHandle, amount, fee, holdType, holdHrs, note }) {

  // ── Security checks ──────────────────────────────────────────

  // 1. Single transaction limit
  if (amount > MAX_SINGLE_TX) {
    throw new Error(`Maximum single transaction is \u20B91,00,000. Contact support to increase your limit.`);
  }

  // 2. Daily limit check
  await checkAndUpdateDailyLimit(senderUid, amount + fee);

  // 3. Rate limit check
  await checkRateLimit(senderUid);

  // ── Payment logic ─────────────────────────────────────────────

  const releaseAt = holdHrs > 0
    ? new Date(Date.now() + holdHrs * 3600000)
    : null;

  const holdMins = holdHrs * 60;
  const holdLabel =
    holdType === "instant"    ? "Instant" :
    holdType === "hold_24"    ? "24hr Hold" :
    holdType === "hold_48"    ? "48hr Hold" :
    holdType === "custom_hr"  ? `${holdHrs}hr Hold` :
    holdType === "custom_min" ? (holdMins < 60 ? `${Math.round(holdMins)}min Hold` : `${holdHrs.toFixed(1)}hr Hold`) :
    "Instant";

  const label = `${holdLabel} → ${recipientHandle}`;

  const deliveryTimeDisplay = releaseAt
    ? `Delivers at ${releaseAt.toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit", hour12: true,
        timeZoneName: "short"
      })}`
    : "Delivered instantly";

  // Deduct from sender
  await updateBalance(senderUid, -(amount + fee));

  // Record sender transaction
  await addTransaction(senderUid, {
    type:               holdType === "instant" ? "debit" : "hold",
    label,
    amount,
    fee,
    status:             holdType,
    holdType,
    releaseAt:          releaseAt ? Timestamp.fromDate(releaseAt) : null,
    deliveryTimeDisplay,
    icon:               holdType === "instant" ? "💸" : "⏳",
    note:               note || "",
    recipient:          recipientHandle,
  });

  // Notify sender
  await addNotification(senderUid, {
    text: holdType === "instant"
      ? `✅ You sent $${amount.toFixed(2)} to ${recipientHandle}`
      : `⏳ $${amount.toFixed(2)} on ${holdLabel} to ${recipientHandle} — ${deliveryTimeDisplay}`,
    forStranger: false,
  });

  // Find recipient and credit them
  try {
    let recipientDoc = null;

    const emailQ = query(collection(db, "users"), where("email", "==", recipientHandle.toLowerCase().trim()));
    const emailSnap = await getDocs(emailQ);
    if (!emailSnap.empty) recipientDoc = emailSnap.docs[0];

    if (!recipientDoc) {
      const idQ = query(collection(db, "users"), where("userId", "==", recipientHandle.toUpperCase().trim()));
      const idSnap = await getDocs(idQ);
      if (!idSnap.empty) recipientDoc = idSnap.docs[0];
    }

    if (!recipientDoc) {
      const nameQ = query(collection(db, "users"), where("name", "==", recipientHandle.trim()));
      const nameSnap = await getDocs(nameQ);
      if (!nameSnap.empty) recipientDoc = nameSnap.docs[0];
    }

    if (recipientDoc) {
      const recipientUid = recipientDoc.id;
      const senderName   = senderData?.name || "Someone";

      if (holdType === "instant") {
        await updateBalance(recipientUid, amount);
        await addTransaction(recipientUid, {
          type:   "credit",
          label:  `Received from ${senderName}`,
          amount,
          fee:    0,
          status: "completed",
          icon:   "💰",
          note:   note || "",
        });
        await addNotification(recipientUid, {
          text:        `💰 ${senderName} sent you $${amount.toFixed(2)}${note ? ` — "${note}"` : ""}`,
          forStranger: true,
        });
      } else {
        await addNotification(recipientUid, {
          text: `⏳ ${senderName} placed $${amount.toFixed(2)} on hold for you — ${deliveryTimeDisplay}`,
          forStranger: true,
        });

        const msDelay = holdHrs * 3600000;
        setTimeout(async () => {
          try {
            await updateBalance(recipientUid, amount);
            await addTransaction(recipientUid, {
              type:   "credit",
              label:  `Received from ${senderName}`,
              amount,
              fee:    0,
              status: "completed",
              icon:   "💰",
              note:   note || "",
            });
            await addNotification(recipientUid, {
              text:        `💰 $${amount.toFixed(2)} from ${senderName} has been released!`,
              forStranger: true,
            });
            await addNotification(senderUid, {
              text:        `✅ Your $${amount.toFixed(2)} hold to ${recipientHandle} was delivered!`,
              forStranger: false,
            });
          } catch (err) {
            console.error("[XyloPay] Hold release failed:", err);
          }
        }, msDelay);
      }
    }
  } catch (recipientErr) {
    console.error("[XyloPay] Recipient credit error:", recipientErr);
  }
}

// ── Add Money ─────────────────────────────────────────────────

export async function addMoney(uid, amount, bankName) {
  await updateBalance(uid, amount);
  await addTransaction(uid, {
    type:   "credit",
    label:  `From ${bankName}`,
    amount,
    fee:    0,
    status: "completed",
    icon:   "🏦",
  });
  await addNotification(uid, {
    text:        `$${amount.toFixed(2)} added from ${bankName}`,
    forStranger: false,
  });
}

// ── Re-attach pending holds ───────────────────────────────────

export async function reattachPendingHolds(senderUid, senderName) {
  try {
    const txRef = query(
      collection(db, "users", senderUid, "transactions"),
      where("type", "==", "hold")
    );
    const snap = await getDocs(txRef);

    snap.forEach(async (docSnap) => {
      const tx = docSnap.data();
      if (!tx.releaseAt) return;

      const releaseDate = tx.releaseAt.toDate ? tx.releaseAt.toDate() : new Date(tx.releaseAt);
      const msRemaining = releaseDate.getTime() - Date.now();
      if (msRemaining <= 0) return;

      try {
        let recipientDoc = null;
        const emailQ = query(collection(db, "users"), where("email", "==", (tx.recipient || "").toLowerCase().trim()));
        const emailSnap = await getDocs(emailQ);
        if (!emailSnap.empty) recipientDoc = emailSnap.docs[0];

        if (!recipientDoc) {
          const idQ = query(collection(db, "users"), where("userId", "==", (tx.recipient || "").toUpperCase().trim()));
          const idSnap = await getDocs(idQ);
          if (!idSnap.empty) recipientDoc = idSnap.docs[0];
        }

        if (recipientDoc) {
          const recipientUid = recipientDoc.id;
          setTimeout(async () => {
            try {
              await updateBalance(recipientUid, tx.amount);
              await addTransaction(recipientUid, {
                type: "credit", label: `Received from ${senderName}`,
                amount: tx.amount, fee: 0, status: "completed", icon: "💰",
              });
              await addNotification(recipientUid, {
                text: `💰 $${tx.amount.toFixed(2)} from ${senderName} has been released!`,
                forStranger: true,
              });
              await addNotification(senderUid, {
                text: `✅ Your $${tx.amount.toFixed(2)} hold to ${tx.recipient} was delivered!`,
                forStranger: false,
              });
            } catch (e) { console.error("[XyloPay] Re-attach release failed:", e); }
          }, msRemaining);
        }
      } catch (e) { console.error("[XyloPay] Re-attach error:", e); }
    });
  } catch (e) {
    console.error("[XyloPay] reattachPendingHolds error:", e);
  }
}
